import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLangue } from '../i18n/LangueContext';

const VOL_MAX = 5000;
const DUR_MAX = 60;

const Besoin = () => {
  const { t, langue } = useLangue();
  const navigate = useNavigate();

  const [source, setSource] = useState('deux');
  const [secteurIdx, setSecteurIdx] = useState(9);
  const [tachesIdx, setTachesIdx] = useState([14, 15, 16]);
  const [volume, setVolume] = useState(400);
  const [duree, setDuree] = useState(4);

  const libSource = t.besoin.sources[source === 'deux' ? 'deux' : source];
  const experts = source !== 'reelles';
  const reel = source !== 'experts';
  const secteur = t.secteurs[secteurIdx];
  const tachesChoisies = tachesIdx.map((i) => t.tachesBesoin[i]);

  const libVolume = useMemo(() => {
    const n = volume.toLocaleString(langue === 'fr' ? 'fr-FR' : 'en-US');
    const unite = volume > 1 ? t.besoin.heures : t.besoin.heure;
    return volume >= VOL_MAX ? `${n} ${unite} ${t.besoin.ouPlus}` : `${n} ${unite}`;
  }, [volume, langue, t]);

  const libDuree = useMemo(() => {
    const unite = duree > 1 ? t.besoin.minutes : t.besoin.minute;
    return duree >= DUR_MAX ? `${duree} ${unite} ${t.besoin.ouPlus}` : `${duree} ${unite}`;
  }, [duree, t]);

  const libTaches = `${t.besoin.nombres[tachesIdx.length] || tachesIdx.length} ${
    tachesIdx.length > 1 ? t.besoin.tachesSel : t.besoin.tacheSel
  }`;

  const basculerTache = (i) => {
    setTachesIdx((prec) => {
      if (prec.includes(i)) {
        if (prec.length === 1) return prec;
        return prec.filter((x) => x !== i);
      }
      return [...prec, i].sort((a, b) => a - b);
    });
  };

  const transmettre = () => {
    const lignes = [
      `${t.besoin.rNature} : ${libSource}`,
      experts ? `${t.besoin.rSecteur} : ${secteur}` : null,
      reel ? `${t.besoin.rTaches} : ${tachesChoisies.join(', ')}` : null,
      `${t.besoin.rVolume} : ${libVolume}`,
      `${t.besoin.rSequences} : ${libDuree}`
    ].filter(Boolean);
    navigate('/demander-un-acces', { state: { besoin: lignes.join('\n') } });
  };

  return (
    <div className="besoin">
      <div className="besoin-form">
        <div className="champ">
          <div className="titre-champ">{t.besoin.natureTitre}</div>
          <p className="aide">{t.besoin.natureAide}</p>
          <div className="bascule jetons" role="group" aria-label={t.besoin.natureTitre}>
            {['experts', 'reelles', 'deux'].map((cle) => (
              <button
                key={cle}
                type="button"
                className="jeton"
                aria-pressed={source === cle}
                onClick={() => setSource(cle)}
              >
                {t.besoin.sources[cle]}
              </button>
            ))}
          </div>
        </div>

        {experts && (
          <div className="champ">
            <label htmlFor="secteur">
              {t.besoin.secteurLabel} <span className="valeur">{secteur}</span>
            </label>
            <p className="aide">{t.besoin.secteurAide}</p>
            <select id="secteur" value={secteurIdx} onChange={(e) => setSecteurIdx(Number(e.target.value))}>
              {t.secteurs.map((s, i) => (
                <option key={s} value={i}>{s}</option>
              ))}
            </select>
          </div>
        )}

        {reel && (
          <div className="champ">
            <div className="titre-champ">
              {t.besoin.tachesTitre} <span className="valeur">{libTaches}</span>
            </div>
            <p className="aide">{t.besoin.tachesAide}</p>
            <div className="jetons" role="group" aria-label={t.besoin.tachesTitre}>
              {t.tachesBesoin.map((v, i) => (
                <button
                  key={v}
                  type="button"
                  className="jeton"
                  aria-pressed={tachesIdx.includes(i)}
                  onClick={() => basculerTache(i)}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="champ">
          <label htmlFor="volume">
            {t.besoin.volumeLabel} <span className="valeur">{libVolume}</span>
          </label>
          <input
            type="range"
            id="volume"
            min={20}
            max={VOL_MAX}
            step={20}
            value={Math.min(volume, VOL_MAX)}
            onChange={(e) => setVolume(Number(e.target.value))}
          />
          <div className="bornes">
            <span>{t.besoin.volumeMin}</span>
            <span>{t.besoin.sansLimite}</span>
          </div>
          <div className="saisie-exacte">
            <label htmlFor="volumeExact">{t.besoin.valeurExacte}</label>
            <input
              id="volumeExact"
              type="number"
              min={1}
              step={10}
              className="saisie"
              value={volume}
              onChange={(e) => setVolume(Math.max(1, Number(e.target.value) || 1))}
            />
            <span className="unite-exacte">{t.besoin.heures}</span>
          </div>
          <p className="aide">{t.besoin.volumeAide}</p>
        </div>

        <div className="champ">
          <label htmlFor="duree">
            {t.besoin.dureeLabel} <span className="valeur">{libDuree}</span>
          </label>
          <input
            type="range"
            id="duree"
            min={1}
            max={DUR_MAX}
            step={1}
            value={Math.min(duree, DUR_MAX)}
            onChange={(e) => setDuree(Number(e.target.value))}
          />
          <div className="bornes">
            <span>{t.besoin.dureeCourte}</span>
            <span>{t.besoin.sansLimite}</span>
          </div>
          <div className="saisie-exacte">
            <label htmlFor="dureeExacte">{t.besoin.valeurExacte}</label>
            <input
              id="dureeExacte"
              type="number"
              min={1}
              step={1}
              className="saisie"
              value={duree}
              onChange={(e) => setDuree(Math.max(1, Number(e.target.value) || 1))}
            />
            <span className="unite-exacte">{t.besoin.minutes}</span>
          </div>
          <p className="aide">{t.besoin.dureeAide}</p>
        </div>
      </div>

      <aside className="recap">
        <div className="recap-tete">{t.besoin.recapTete}</div>
        <div className="recap-corps">
          <dl>
            <dt>{t.besoin.rNature}</dt>
            <dd>{libSource}</dd>
          </dl>

          {experts && (
            <div>
              <div className="recap-sep" />
              <p className="recap-titre">{t.besoin.rBlocExperts}</p>
              <dl>
                <dt>{t.besoin.rSecteur}</dt>
                <dd>{secteur}</dd>
              </dl>
            </div>
          )}

          {reel && (
            <div>
              <div className="recap-sep" />
              <p className="recap-titre">{t.besoin.rBlocReel}</p>
              <dl>
                <dt>{t.besoin.rTaches}</dt>
                <dd className="multi">{tachesChoisies.join(', ')}</dd>
              </dl>
            </div>
          )}

          <div className="recap-sep" />
          <p className="recap-titre">{experts && reel ? t.besoin.rCommun : t.besoin.rCarac}</p>
          <dl>
            <dt>{t.besoin.rVolume}</dt>
            <dd>{libVolume}</dd>
            <dt>{t.besoin.rSequences}</dt>
            <dd>{libDuree}</dd>
          </dl>
        </div>
        <div className="recap-reste">{t.besoin.reste}</div>
        <div className="recap-pied">
          <button type="button" className="btn btn-plein" style={{ justifyContent: 'center' }} onClick={transmettre}>
            {t.besoin.cta}
          </button>
          <p className="recap-note">{t.besoin.note}</p>
        </div>
      </aside>
    </div>
  );
};

export default Besoin;
